import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RestaurantHeader from '../../components/navigation/RestaurantHeader';
import SearchBar from './components/SearchBar';
import CategoryFilter from './components/CategoryFilter';
import CategorySection from './components/CategorySection';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';
import PromotionsPopup from './components/PromotionsPopup';
import DishDetailsModal from './components/DishDetailsModal';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDishFilters } from '../../hooks/useDishFilters';
import { calculateCategoryCounts, groupDishesByCategory } from '../../utils/dishHelpers';
import ErrorToast from '../../components/ui/ErrorToast';
import { trackQRScan } from '../../services/dishStatsService';
import {
  fetchDishes,
  fetchCategories,
  fetchRestaurantProfile,
  subscribeToMenuDishes,
  subscribeToCategories,
  subscribeToRestaurantProfile,
} from '../../services/menuDataService';
import { fetchPromotions } from '../../services/promotionsService';
import { restaurantProfile as defaultRestaurantProfile } from '../../data/restaurantData';
import { fetchWithTimeout } from '../../lib/fetchWithTimeout';

// Helper to generate unique toast IDs
let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;

const PROMO_POPUP_SESSION_KEY = 'qrmenu_promo_popup_shown';

function hasShownPromoPopupThisSession() {
  try {
    return sessionStorage.getItem(PROMO_POPUP_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markPromoPopupShownThisSession() {
  try {
    sessionStorage.setItem(PROMO_POPUP_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
}

const RestaurantMenuLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Promotions popup state
  const [promotions, setPromotions] = useState([]);
  const [showPromotionsPopup, setShowPromotionsPopup] = useState(false);

  const [selectedDish, setSelectedDish] = useState(null);

  // Error toasts state
  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = (message, type = 'error') =>
    setErrorToasts((prev) => {
      if (prev.some((t) => t.message === message && t.type === type)) return prev;
      return [...prev, { id: newToastId(), message, type }];
    });
  const dismissToast = (id) =>
    setErrorToasts(prev => prev?.filter(t => t?.id !== id));

  // ── Restaurant profile from Supabase ──────────────────────────────────────
  const [restaurantProfile, setRestaurantProfile] = useState(defaultRestaurantProfile);

  // ── Dishes from Supabase ──────────────────────────────────────────────────
  const [dishes, setDishes] = useState([]);

  // ── Categories from Supabase ──────────────────────────────────────────────
  const [adminCategories, setAdminCategories] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadPart = async (label, fn, fallback) => {
      try {
        return await fetchWithTimeout(fn, 12000, label);
      } catch (err) {
        console.warn(`Menu load (${label}):`, err);
        return fallback;
      }
    };

    (async () => {
      const [profile, dishList, catList, promoList] = await Promise.all([
        loadPart('profile', fetchRestaurantProfile, null),
        loadPart('dishes', fetchDishes, []),
        loadPart('categories', fetchCategories, null),
        loadPart('promotions', () => fetchPromotions(true), []),
      ]);

      if (cancelled) return;

      if (profile) setRestaurantProfile(profile);
      setDishes((dishList || []).filter((d) => !d?.hidden));
      if (catList) setAdminCategories(catList);

      if (promoList?.length > 0) {
        setPromotions(promoList);
        const skipPromo =
          location.state?.fromAR === true ||
          location.state?.skipPromo === true ||
          hasShownPromoPopupThisSession();
        if (!skipPromo) {
          setShowPromotionsPopup(true);
          markPromoPopupShownThisSession();
        }
      }

      const nothingLoaded =
        !profile && (!dishList || dishList.length === 0) && !catList;
      if (nothingLoaded) {
        addToast(
          'Impossible de charger le menu (réseau lent ou Supabase injoignable). Réessayez.',
          'warning'
        );
      }

      setIsLoading(false);
    })();

    // Real-time subscriptions
    const dishChannel = subscribeToMenuDishes(() => {
      fetchDishes()?.then(list => setDishes(list?.filter(d => !d?.hidden)))?.catch(() => {});
    });

    const catChannel = subscribeToCategories(() => {
      fetchCategories()?.then(setAdminCategories)?.catch(() => {});
    });

    const profileChannel = subscribeToRestaurantProfile(() => {
      fetchRestaurantProfile()?.then(setRestaurantProfile)?.catch(() => {});
    });

    return () => {
      cancelled = true;
      try { dishChannel?.unsubscribe(); } catch (e) {}
      try { catChannel?.unsubscribe(); } catch (e) {}
      try { profileChannel?.unsubscribe(); } catch (e) {}
    };
  }, []);

  // Build categories list
  const categories = adminCategories
    ? [
        { id: 'all', name: t('allDishes'), description: '' },
        ...adminCategories?.map(cat => ({
          id: cat?.id,
          name: cat?.name,
          description: '',
        }))
      ]
    : [
        { id: 'all', name: t('allDishes'), description: '' },
        { id: 'appetizers', name: t('appetizers'), description: '' },
        { id: 'mains', name: t('mainCourse'), description: '' },
        { id: 'desserts', name: t('desserts'), description: '' },
        { id: 'beverages', name: t('beverages'), description: '' },
      ];

  // Use custom hook for filtering
  const filteredDishes = useDishFilters(dishes, activeCategory, searchQuery);

  // Calculate category counts
  const categoriesWithCounts = calculateCategoryCounts(categories, dishes);

  // Group dishes by category
  const groupedDishes = groupDishesByCategory(filteredDishes, categories, activeCategory);

  // Track QR scan only when user enters via ?source=qr
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params?.get('source') === 'qr') {
      trackQRScan('restaurant', 'Restaurant QR Scan');
    }
  }, []);

  const dishHasAR = (dish) => !!(dish?.arUrl && String(dish.arUrl).trim());

  const handleViewAR = (dish) => {
    if (!dishHasAR(dish)) {
      addToast('Modèle 3D non disponible pour ce plat.', 'warning');
      return;
    }
    navigate('/ar-viewer', { state: { dish } });
  };

  const handleViewDetails = (dish) => {
    setSelectedDish(dish);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories?.find((c) => c?.id === categoryId);
    return cat?.name || categoryId || '';
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  if (isLoading) {
    return (
      <div className="restaurant-menu-landing min-h-screen bg-background">
        <RestaurantHeader
          restaurantName={restaurantProfile?.name}
          restaurantLogo={restaurantProfile?.logo}
          tagline={restaurantProfile?.tagline}
        />
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="restaurant-menu-landing min-h-screen bg-background">
      <RestaurantHeader
        restaurantName={restaurantProfile?.name}
        restaurantLogo={restaurantProfile?.logo}
        tagline={restaurantProfile?.tagline}
      />
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
        <div className="menu-controls-section mb-6 md:mb-8 space-y-4 md:space-y-6">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            resultCount={filteredDishes?.length}
          />

          <CategoryFilter
            categories={categoriesWithCounts}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        <div className="menu-content-section">
          {filteredDishes?.length === 0 ? (
            <EmptyState
              searchQuery={searchQuery}
              onClearSearch={handleClearSearch}
            />
          ) : (
            <div className="menu-categories-container space-y-8 md:space-y-10 lg:space-y-12">
              {Object.entries(groupedDishes)?.map(([categoryId, categoryDishes]) => {
                const category = categories?.find(cat => cat?.id === categoryId);
                return (
                  <CategorySection
                    key={categoryId}
                    category={category}
                    dishes={categoryDishes}
                    onViewAR={handleViewAR}
                    onViewDetails={handleViewDetails}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-10 mt-8 border-t border-border bg-card">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-2 text-center mb-6">
            <h2 className="text-xl font-bold text-foreground">{restaurantProfile?.name}</h2>
            {restaurantProfile?.tagline && (
              <p className="text-sm italic text-muted-foreground">{restaurantProfile?.tagline}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center max-w-2xl mx-auto">
            {restaurantProfile?.phone && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Téléphone</p>
                <p className="text-sm text-foreground">{restaurantProfile?.phone}</p>
              </div>
            )}
            {restaurantProfile?.email && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Adresse e-mail</p>
                <p className="text-sm text-foreground">{restaurantProfile?.email}</p>
              </div>
            )}
            {restaurantProfile?.address && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Adresse</p>
                <p className="text-sm text-foreground">{restaurantProfile?.address}</p>
              </div>
            )}
          </div>
          <div className="flex justify-center mt-6">
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs font-semibold text-foreground tracking-widest">SCANN3R</span>
              <span className="text-xs text-foreground opacity-70">felix.gagnon@scann3r.ca</span>
              <button
                onClick={() => navigate('/restaurant-admin-dashboard')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
              >
                Admin
              </button>
              <button
                onClick={() => navigate('/modalites-politiques')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
              >
                Modalités et Politiques
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Error Toasts */}
      <ErrorToast toasts={errorToasts} onDismiss={dismissToast} />

      {/* Promotions Popup */}
      {showPromotionsPopup && promotions?.length > 0 && (
        <PromotionsPopup
          promotions={promotions}
          onClose={() => {
            setShowPromotionsPopup(false);
            markPromoPopupShownThisSession();
          }}
        />
      )}

      {selectedDish && (
        <DishDetailsModal
          dish={selectedDish}
          categoryName={getCategoryName(selectedDish?.category)}
          hasARModel={dishHasAR(selectedDish)}
          onViewAR={handleViewAR}
          onClose={() => setSelectedDish(null)}
        />
      )}
    </div>
  );
};

export default RestaurantMenuLanding;